/**
 * หน้าจอ "บอร์ด Kanban" (Kanban Signal) — P2-7f ครึ่งล่าง, gate ด้วย
 * `KANBAN_EXECUTION` เท่านั้น (D-102 ข้อ 1)
 *
 * ปิดหกในหกเส้นทางของ `KANBAN_EXECUTION` ที่ `apps/api/.../modules/kanban/router.py`
 * serve จริง: `GET`/`POST /v1/kanban-signals`, `GET .../{signal_id}`, `empty`,
 * `acknowledge`, `start-replenishment`, `replenish`, `cancel`
 *
 * **สี่ข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้ (invariant ของ
 * `WO-P2-M7-006`):**
 *
 * 1. **นี่คือครึ่งเดียวของ D-102 — ครึ่งที่ทำงานตามที่ policy วางไว้ ไม่ใช่ตั้ง policy
 *    เอง** ไม่มีฟังก์ชันใดในไฟล์นี้เรียก `lib/kanban/client.ts` ฝั่ง policy/cycle/card
 *    เลย — การออกการ์ดและตัด control cycle เป็นของ `ReplenishmentPolicyScreen`
 *    เท่านั้น
 * 2. **สัญญาณคือคำขอให้เติมของ ไม่ใช่การเคลื่อนสต๊อก** — มีแต่ `replenish` เท่านั้น
 *    ที่เขียน ledger `empty`/`acknowledge`/`start-replenishment` ขยับแค่สถานะของ
 *    signal/การ์ดเอง ป้ายกำกับ &quot;ไม่เคลื่อนสต๊อก&quot; แสดงอยู่ข้างปุ่มสามปุ่มนั้น
 *    ตรง ๆ
 * 3. **`409` กับ `422` บนเส้นทาง signal ต่างกัน และห้ามแสดงเหมือนกัน**
 *    (`docs/API_CONTRACT.md`): `409 SIGNAL_STATUS_CONFLICT` คือแถวขยับใต้มือ — ชวน
 *    โหลดรายการใหม่แล้วลองอีกครั้ง ส่วน `422 SIGNAL_IS_NOT_OPEN` คือ signal จบไปแล้ว —
 *    **ไม่มีปุ่มลองอีกครั้งให้กด** เพราะลองเท่าไรก็ไม่มีทางสำเร็จ
 * 4. **ยกเลิกต้องมีเหตุผลก่อนปุ่มจะกดได้** (WO-P2-M7-005) — ปุ่มยืนยันการยกเลิก
 *    ถูก `disabled` จนกว่าจะกรอกเหตุผล ไม่ใช่ส่งสตริงว่างไปให้เซิร์ฟเวอร์ปฏิเสธเอง
 *
 * โครงยืมมาจาก `PurchaseRequisitionScreen`/`ReplenishmentPolicyScreen` — ช่องค้นหา
 * บนสุด, ฟอร์มสแกนเบิกอยู่หลังปุ่มในกล่อง `Modal`, ปุ่ม lifecycle รายแถวที่ transition
 * ซึ่งต้องการข้อมูลเพิ่ม (`start-replenishment`/`replenish`/`cancel`) เปิดฟอร์มย่อย
 * ใต้แถวนั้นแทนที่จะยิงทันที
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  acknowledgeKanbanSignal,
  cancelKanbanSignal,
  currentStatusFromFailure,
  fetchKanbanSignals,
  replenishKanbanSignal,
  ruleFromFailure,
  scanKanbanCardEmpty,
  startKanbanReplenishment,
  type ApiErrorEnvelope,
  type KanbanFailure,
  type KanbanSignalTransition,
  type KanbanSignalWriteResult,
} from '../../lib/kanban/client';
import {
  filterKanbanSignals,
  isValidCancellationReason,
  movesInventory,
  signalActionLabel,
  signalActionTargetStatus,
  signalLifecycleActionsFor,
  signalStatusLabel,
  signalStatusTone,
  toKanbanContainerLotInputs,
  type KanbanLotDraft,
} from '../../lib/kanban/kanbanView';
import {
  CARD_ALREADY_HAS_AN_OPEN_SIGNAL,
  CARD_CANNOT_SIGNAL,
  CARD_CODE_MAX_LENGTH,
  CARD_NOT_FOUND,
  CLIENT_EVENT_ID_ALREADY_USED,
  DUPLICATE_LOT_IN_CONTAINER,
  KANBAN_EXECUTION_MODULE,
  LOT_IN_ANOTHER_PLANT,
  LOT_IS_NOT_THE_CARD_PRODUCT,
  LOT_NOT_FOUND,
  PART_ALREADY_COVERED_BY_PUSH_SUPPLY,
  PLANT_OUTSIDE_SCOPE,
  PRODUCTION_ORDER_SUPPLY_DOCUMENT,
  SIGNAL_FIELD_IS_SERVER_OWNED,
  SIGNAL_IS_NOT_OPEN,
  SIGNAL_STATUS_CONFLICT,
  STOCK_TRANSFER_SUPPLY_DOCUMENT,
  type KanbanSignal,
} from '../../lib/kanban/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type SignalFailureContext = 'signals' | 'empty' | KanbanSignalTransition;

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
  /** `409` เท่านั้นที่ควรเสนอปุ่ม "ลองอีกครั้ง" — `422` ลองเท่าไรก็ไม่สำเร็จ */
  readonly offerRetry: boolean;
}

function isWriteContext(context: SignalFailureContext): boolean {
  return context !== 'signals';
}

function ruleCopy(rule: string | null, currentStatus: string | null): FailureCopy | null {
  switch (rule) {
    case SIGNAL_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${SIGNAL_FIELD_IS_SERVER_OWNED} — แจ้งผู้ดูแลระบบ`,
        offerRetry: false,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'การ์ด/สัญญาณนี้อยู่นอกขอบเขตโรงงานของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} (D-004)`,
        offerRetry: false,
      };
    case CARD_NOT_FOUND:
      return {
        heading: 'ไม่พบการ์ดรหัสนี้ในทะเบียน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${CARD_NOT_FOUND} — การ์ดต้องถูกออกจากหน้าจอ "ตั้งค่า Kanban" ก่อนจึงจะสแกนเบิกได้`,
        offerRetry: false,
      };
    case CARD_ALREADY_HAS_AN_OPEN_SIGNAL:
      return {
        heading: 'การ์ดนี้มีสัญญาณที่ยังเปิดอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${CARD_ALREADY_HAS_AN_OPEN_SIGNAL} — หนึ่งการ์ดมี signal เปิดได้ไม่เกินหนึ่งตัว โหลดรายการใหม่เพื่อดูสัญญาณที่เปิดอยู่`,
        offerRetry: true,
      };
    case CARD_CANNOT_SIGNAL:
      return {
        heading: 'สถานะปัจจุบันของการ์ดสแกนเบิกไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${CARD_CANNOT_SIGNAL}`,
        offerRetry: true,
      };
    case PART_ALREADY_COVERED_BY_PUSH_SUPPLY:
      return {
        heading: 'ชิ้นนี้ยังมีเอกสาร Push เปิดอยู่ — เติมด้วย Kanban อีกทางจะเติมซ้ำ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PART_ALREADY_COVERED_BY_PUSH_SUPPLY} (D-102 ข้อ 8b) — ปิดหรือยกเลิกเอกสารนั้นก่อน ไม่มีการลองใหม่ครั้งไหนที่จะสำเร็จจนกว่าจะทำเช่นนั้น`,
        offerRetry: false,
      };
    case CLIENT_EVENT_ID_ALREADY_USED:
      return {
        heading: 'รหัสเหตุการณ์นี้เคยใช้กับการ์ดอื่นไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${CLIENT_EVENT_ID_ALREADY_USED}`,
        offerRetry: true,
      };
    case SIGNAL_STATUS_CONFLICT:
      return {
        heading: 'สัญญาณนี้ขยับสถานะไปแล้วใต้มือคุณ',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${SIGNAL_STATUS_CONFLICT}${
          currentStatus === null ? '' : ` — ขณะนี้อยู่ที่ ${signalStatusLabel(currentStatus)}`
        } — นี่คือการแพ้ race ธรรมดา โหลดรายการใหม่แล้ว**ลองอีกครั้งได้**`,
        offerRetry: true,
      };
    case SIGNAL_IS_NOT_OPEN:
      return {
        heading: 'สัญญาณนี้จบ lifecycle ไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SIGNAL_IS_NOT_OPEN}${
          currentStatus === null ? '' : ` — สถานะปัจจุบันคือ ${signalStatusLabel(currentStatus)}`
        } — **ต่างจากแถวขยับใต้มือโดยตั้งใจ**: นี่ไม่ใช่การแพ้ race และไม่มีการลองใหม่ครั้งไหนที่จะสำเร็จ (ของที่เติมแล้วเติมแล้ว ของที่ยกเลิกแล้วยกเลิกแล้ว)`,
        offerRetry: false,
      };
    case LOT_NOT_FOUND:
      return {
        heading: 'ไม่พบ lot ที่ระบุ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_NOT_FOUND}`,
        offerRetry: false,
      };
    case LOT_IN_ANOTHER_PLANT:
      return {
        heading: 'lot ที่ระบุเป็นของโรงงานอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_IN_ANOTHER_PLANT}`,
        offerRetry: false,
      };
    case LOT_IS_NOT_THE_CARD_PRODUCT:
      return {
        heading: 'lot ที่ระบุไม่ใช่สินค้าตัวเดียวกับที่ loop นี้เติม',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_IS_NOT_THE_CARD_PRODUCT}`,
        offerRetry: false,
      };
    case DUPLICATE_LOT_IN_CONTAINER:
      return {
        heading: 'ระบุ lot เดียวกันซ้ำสองบรรทัด',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DUPLICATE_LOT_IN_CONTAINER} — รวมจำนวนไว้บรรทัดเดียว`,
        offerRetry: false,
      };
    default:
      return null;
  }
}

function failureCopy(failure: KanbanFailure, context: SignalFailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const currentStatus = currentStatusFromFailure(failure);
  const nothingChanged = writing ? ' ไม่มีอะไรถูกเขียนและไม่มีแถว audit ถูกเพิ่ม' : '';

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)',
        offerRetry: false,
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${KANBAN_EXECUTION_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
          offerRetry: false,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบ signal ที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ไม่มีอยู่แล้ว หรืออยู่นอกขอบเขตของคุณ${nothingChanged}`,
        offerRetry: false,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — แถวขยับใต้มือคุณ ลองอีกครั้งได้${nothingChanged}`,
          offerRetry: true,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 — ไม่ใช่การแพ้ race การลองใหม่แบบเดิมจะไม่สำเร็จ${nothingChanged}`,
          offerRetry: false,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
          offerRetry: false,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
        offerRetry: true,
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่`,
        offerRetry: true,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ',
        offerRetry: false,
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
        offerRetry: false,
      };
  }
}

function envelopeOf(failure: KanbanFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

export interface SignalFailureNoticeProps {
  readonly failure: KanbanFailure;
  readonly context: SignalFailureContext;
  readonly onRetry?: () => void;
}

export function SignalFailureNotice({ failure, context, onRetry }: SignalFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  const isConflict = failure.status === 'conflict';
  const isRuleViolation = failure.status === 'rule-violation';

  return (
    <div
      className="cap-notice"
      data-testid="signal-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={isConflict ? 'info' : isRuleViolation ? 'warning' : 'danger'}>
        {isConflict ? 'แถวขยับใต้มือ — ลองใหม่ได้' : isRuleViolation ? 'จบไปแล้ว — ลองใหม่ไม่ได้' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="signal-rule">
            {rule}
          </code>
        </p>
      )}
      {serverMessage === null ? null : (
        <p className="cap-notice__note">ข้อความจากเซิร์ฟเวอร์: {serverMessage}</p>
      )}
      {onRetry === undefined || !copy.offerRetry ? null : (
        <div className="cap-notice__actions">
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="signal-failure-retry"
            onClick={onRetry}
          >
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface KanbanSignalScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type SignalsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly KanbanSignal[] }
  | KanbanFailure;

type Pending =
  | { readonly kind: 'empty' }
  | { readonly kind: KanbanSignalTransition; readonly signalId: string };

interface ActionOutcome {
  readonly signalId: string;
  readonly transition: KanbanSignalTransition;
  readonly failure: KanbanFailure | null;
  readonly signal: KanbanSignal | null;
}

type RowForm =
  | { readonly signalId: string; readonly transition: 'start-replenishment' }
  | { readonly signalId: string; readonly transition: 'replenish' }
  | { readonly signalId: string; readonly transition: 'cancel' };

export function KanbanSignalScreen({ permissions, apiBaseUrl, fetchImpl }: KanbanSignalScreenProps) {
  const canCreate = can(permissions, KANBAN_EXECUTION_MODULE, 'CREATE');
  const canEdit = can(permissions, KANBAN_EXECUTION_MODULE, 'EDIT');
  const canWriteAnything = canCreate || canEdit;

  const [signals, setSignals] = useState<SignalsState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);
  const [query, setQuery] = useState('');

  const [scanOpen, setScanOpen] = useState(false);
  const [scanCardCode, setScanCardCode] = useState('');
  const [scanFailure, setScanFailure] = useState<KanbanFailure | null>(null);
  const [scannedSignal, setScannedSignal] = useState<KanbanSignal | null>(null);

  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);

  const [rowForm, setRowForm] = useState<RowForm | null>(null);
  const [documentType, setDocumentType] = useState<string>(PRODUCTION_ORDER_SUPPLY_DOCUMENT);
  const [documentId, setDocumentId] = useState('');
  const [lots, setLots] = useState<readonly KanbanLotDraft[]>([{ lotId: '', qty: '' }]);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setSignals((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    void fetchKanbanSignals({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setSignals(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openScan = () => {
    if (!canCreate) {
      return;
    }
    setScanCardCode('');
    setScanFailure(null);
    setScannedSignal(null);
    setScanOpen(true);
  };

  const closeScan = useCallback(() => {
    setScanOpen(false);
  }, []);

  const submitScan = async () => {
    const cardCode = scanCardCode.trim();
    if (cardCode === '' || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'empty' });
    setScanFailure(null);
    setScannedSignal(null);

    const result = await scanKanbanCardEmpty({ card_code: cardCode }, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setScanFailure(result);
      return;
    }

    setScannedSignal(result.signal);
    setScanOpen(false);
    setReloadToken((token) => token + 1);
  };

  const closeRowForm = () => {
    setRowForm(null);
    setDocumentType(PRODUCTION_ORDER_SUPPLY_DOCUMENT);
    setDocumentId('');
    setLots([{ lotId: '', qty: '' }]);
    setCancellationReason('');
  };

  const openRowForm = (signal: KanbanSignal, transition: KanbanSignalTransition) => {
    if (transition === 'acknowledge') {
      void runSimpleTransition(signal, transition);
      return;
    }
    setActionOutcome(null);
    setRowForm({ signalId: signal.signal_id, transition });
    setDocumentType(PRODUCTION_ORDER_SUPPLY_DOCUMENT);
    setDocumentId('');
    setLots([{ lotId: '', qty: '' }]);
    setCancellationReason('');
  };

  const applyOutcome = (
    signal: KanbanSignal,
    transition: KanbanSignalTransition,
    result: KanbanSignalWriteResult,
  ) => {
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setActionOutcome({ signalId: signal.signal_id, transition, failure: result, signal: null });
      return;
    }
    setActionOutcome({ signalId: signal.signal_id, transition, failure: null, signal: result.signal });
    setReloadToken((token) => token + 1);
  };

  const runSimpleTransition = async (signal: KanbanSignal, transition: 'acknowledge') => {
    if (pending !== null || !can(permissions, KANBAN_EXECUTION_MODULE, 'EDIT')) {
      return;
    }
    setPending({ kind: transition, signalId: signal.signal_id });
    const result = await acknowledgeKanbanSignal(
      signal.signal_id,
      { expected_status: signal.status },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    applyOutcome(signal, transition, result);
  };

  const submitStartReplenishment = async (signal: KanbanSignal) => {
    const document = documentId.trim();
    if (pending !== null || !canEdit || document === '') {
      return;
    }
    setPending({ kind: 'start-replenishment', signalId: signal.signal_id });
    const result = await startKanbanReplenishment(
      signal.signal_id,
      {
        supply_document_type: documentType as 'PRODUCTION_ORDER' | 'STOCK_TRANSFER',
        supply_document_id: document,
        expected_status: signal.status,
      },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    applyOutcome(signal, 'start-replenishment', result);
    if (result.status === 'ready') {
      closeRowForm();
    }
  };

  const submitReplenish = async (signal: KanbanSignal) => {
    const lotInputs = toKanbanContainerLotInputs(lots);
    if (pending !== null || !canEdit || lotInputs === null) {
      return;
    }
    setPending({ kind: 'replenish', signalId: signal.signal_id });
    const result = await replenishKanbanSignal(
      signal.signal_id,
      { lots: lotInputs, expected_status: signal.status },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    applyOutcome(signal, 'replenish', result);
    if (result.status === 'ready') {
      closeRowForm();
    }
  };

  const submitCancel = async (signal: KanbanSignal) => {
    if (pending !== null || !canEdit || !isValidCancellationReason(cancellationReason)) {
      return;
    }
    setPending({ kind: 'cancel', signalId: signal.signal_id });
    const result = await cancelKanbanSignal(
      signal.signal_id,
      { cancellation_reason: cancellationReason.trim(), expected_status: signal.status },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    applyOutcome(signal, 'cancel', result);
    if (result.status === 'ready') {
      closeRowForm();
    }
  };

  const items = signals.status === 'ready' ? signals.items : [];
  const matchedItems = filterKanbanSignals(items, query);
  const busy = pending !== null;

  const offeredActions = (signal: KanbanSignal): readonly KanbanSignalTransition[] =>
    signalLifecycleActionsFor(signal.status).filter(() =>
      can(permissions, KANBAN_EXECUTION_MODULE, 'EDIT'),
    );

  const anyRowHasActions = matchedItems.some((signal) => offeredActions(signal).length > 0);

  return (
    <div className="cap-page" data-testid="kanban-signals">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            บอร์ด Kanban (Signal)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  สแกนการ์ดเบิก รับทราบ เริ่มเติม และปิดสัญญาณตามที่กติกาของหน้าจอ &quot;ตั้งค่า
                  Kanban&quot; วางไว้ — <strong>signal คือคำขอให้เติมของ ไม่ใช่การเคลื่อน
                  สต๊อก</strong> มีแต่ &quot;เติมของแล้ว&quot; เท่านั้นที่แตะ ledger จริง
                  (D-102)
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="signal-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังขับสัญญาณไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
            หรือ <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{KANBAN_EXECUTION_MODULE}</code> — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
        </div>
      ) : null}

      <div className="cap-filters" data-testid="signal-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหาสัญญาณ</span>
          <input
            className="cap-input"
            type="search"
            data-testid="signal-search"
            value={query}
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหา"
            text="กรองจากรหัสการ์ดหรือรหัสสินค้า — กรองจากรายการที่โหลดมาแล้ว"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="signal-scan-open"
            onClick={openScan}
          >
            สแกนการ์ดเบิก
          </button>
        ) : null}
      </div>

      <Modal open={canCreate && scanOpen} onClose={closeScan} title="สแกนการ์ดเบิก">
        <form
          className="cap-form"
          data-testid="signal-scan-form"
          aria-label="สแกนการ์ดเบิก"
          onSubmit={(event) => {
            event.preventDefault();
            void submitScan();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสการ์ด</span>
            <input
              className="cap-input"
              data-testid="signal-scan-card-code"
              value={scanCardCode}
              maxLength={CARD_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => setScanCardCode(event.target.value)}
            />
            <InfoHint
              label="เกี่ยวกับรหัสการ์ด"
              text="รหัสที่พิมพ์บนป้าย — เซิร์ฟเวอร์เป็นผู้ตัดสินสถานะของการ์ด ไม่ใช่หน้าจอนี้"
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="signal-scan-submit"
              disabled={busy || scanCardCode.trim() === ''}
            >
              {pending?.kind === 'empty' ? 'กำลังส่ง…' : 'ยกสัญญาณ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="signal-scan-cancel"
              disabled={busy}
              onClick={closeScan}
            >
              ยกเลิก
            </button>
          </div>

          {scanFailure === null ? null : <SignalFailureNotice failure={scanFailure} context="empty" />}
        </form>
      </Modal>

      {scannedSignal === null ? null : (
        <div className="cap-notice" data-testid="signal-scan-result" role="status">
          <StatusBadge tone="success">ยกสัญญาณแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            สัญญาณของการ์ด {scannedSignal.card_code} ยกแล้ว สถานะ{' '}
            {signalStatusLabel(scannedSignal.status)}
          </h2>
          <p className="cap-notice__text">ยังไม่มีอะไรเคลื่อนสต๊อก — รอมีคนรับทราบต่อไป</p>
        </div>
      )}

      {actionOutcome?.signal == null ? null : (
        <div className="cap-notice" data-testid="signal-action-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            สัญญาณของการ์ด {actionOutcome.signal.card_code} อยู่ที่สถานะ{' '}
            {signalStatusLabel(actionOutcome.signal.status)} แล้ว
          </h2>
          {movesInventory(actionOutcome.transition) ? (
            <p className="cap-notice__text" data-testid="signal-moved-inventory">
              การกระทำนี้เคลื่อนสต๊อกจริง — ดูรายการ lot ที่บันทึกไว้ในแถวนี้
            </p>
          ) : (
            <p className="cap-notice__text" data-testid="signal-did-not-move-inventory">
              การกระทำนี้ **ไม่ได้** เคลื่อนสต๊อก — ขยับแค่สถานะของสัญญาณ/การ์ดเอง
            </p>
          )}
        </div>
      )}

      {signals.status === 'loading' ? (
        <div className="cap-notice" data-testid="signal-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดบอร์ด Kanban…</p>
        </div>
      ) : null}

      {signals.status !== 'loading' && signals.status !== 'ready' ? (
        <SignalFailureNotice
          failure={signals}
          context="signals"
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      ) : null}

      {signals.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="signal-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีสัญญาณ</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีสัญญาณ Kanban ในโรงงานที่คุณเข้าถึงได้</h2>
        </div>
      ) : null}

      {signals.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="signal-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบสัญญาณที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">ไม่มีสัญญาณที่ตรงกับ &quot;{query.trim()}&quot;</h2>
        </div>
      ) : null}

      {signals.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="สัญญาณ Kanban ทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">สัญญาณ Kanban ทั้งหมด</h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="signal-table">
              <caption className="cap-visually-hidden">
                สัญญาณ Kanban ของโรงงานที่คุณเข้าถึงได้ พร้อมสถานะและปุ่มที่สถานะนั้นอนุญาต
              </caption>
              <thead>
                <tr>
                  <th scope="col">การ์ด</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ยกโดย</th>
                  <th scope="col">สถานะ</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((signal) => {
                  const actions = offeredActions(signal);
                  const outcome =
                    actionOutcome?.signalId === signal.signal_id && actionOutcome.failure !== null
                      ? actionOutcome
                      : null;
                  const openForm = rowForm?.signalId === signal.signal_id ? rowForm : null;

                  return (
                    <tr
                      key={signal.signal_id}
                      data-testid={`signal-row-${signal.signal_id}`}
                      data-status={signal.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{signal.card_code}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{signal.product_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{signal.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{signal.raised_by_employee_id}</code>
                      </td>
                      <td>
                        <StatusBadge tone={signalStatusTone(signal.status)}>
                          {signalStatusLabel(signal.status)}
                        </StatusBadge>
                      </td>
                      {anyRowHasActions ? (
                        <td>
                          {actions.map((transition) => (
                            <button
                              key={transition}
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`signal-action-${transition}-${signal.signal_id}`}
                              disabled={busy}
                              onClick={() => openRowForm(signal, transition)}
                            >
                              {pending?.kind === transition &&
                              'signalId' in pending &&
                              pending.signalId === signal.signal_id
                                ? 'กำลังส่ง…'
                                : signalActionLabel(transition)}
                              {movesInventory(transition) ? '' : ' (ไม่เคลื่อนสต๊อก)'}
                            </button>
                          ))}

                          {openForm?.transition === 'start-replenishment' ? (
                            <form
                              className="cap-form cap-form--inline"
                              data-testid={`signal-start-form-${signal.signal_id}`}
                              aria-label="เริ่มเติม"
                              onSubmit={(event) => {
                                event.preventDefault();
                                void submitStartReplenishment(signal);
                              }}
                            >
                              <select
                                className="cap-input"
                                data-testid="signal-start-document-type"
                                value={documentType}
                                onChange={(event) => setDocumentType(event.target.value)}
                              >
                                <option value={PRODUCTION_ORDER_SUPPLY_DOCUMENT}>ใบสั่งผลิต</option>
                                <option value={STOCK_TRANSFER_SUPPLY_DOCUMENT}>โอนย้ายสต๊อก</option>
                              </select>
                              <input
                                className="cap-input"
                                data-testid="signal-start-document-id"
                                placeholder="UUID ของเอกสาร"
                                value={documentId}
                                onChange={(event) => setDocumentId(event.target.value)}
                              />
                              <button
                                type="submit"
                                className="cap-button cap-button--primary"
                                data-testid="signal-start-submit"
                                disabled={busy || documentId.trim() === ''}
                              >
                                ยืนยันเริ่มเติม
                              </button>
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid="signal-start-close"
                                onClick={closeRowForm}
                              >
                                ปิด
                              </button>
                            </form>
                          ) : null}

                          {openForm?.transition === 'replenish' ? (
                            <form
                              className="cap-form cap-form--inline"
                              data-testid={`signal-replenish-form-${signal.signal_id}`}
                              aria-label="เติมของแล้ว"
                              onSubmit={(event) => {
                                event.preventDefault();
                                void submitReplenish(signal);
                              }}
                            >
                              {lots.map((lot, index) => (
                                <span key={index} className="cap-form__row">
                                  <input
                                    className="cap-input"
                                    data-testid={`signal-replenish-lot-id-${index}`}
                                    placeholder="lot_id"
                                    value={lot.lotId}
                                    onChange={(event) =>
                                      setLots((previous) =>
                                        previous.map((entry, entryIndex) =>
                                          entryIndex === index
                                            ? { ...entry, lotId: event.target.value }
                                            : entry,
                                        ),
                                      )
                                    }
                                  />
                                  <input
                                    className="cap-input"
                                    data-testid={`signal-replenish-lot-qty-${index}`}
                                    placeholder="จำนวน"
                                    inputMode="decimal"
                                    value={lot.qty}
                                    onChange={(event) =>
                                      setLots((previous) =>
                                        previous.map((entry, entryIndex) =>
                                          entryIndex === index
                                            ? { ...entry, qty: event.target.value }
                                            : entry,
                                        ),
                                      )
                                    }
                                  />
                                </span>
                              ))}
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid="signal-replenish-add-lot"
                                onClick={() =>
                                  setLots((previous) => [...previous, { lotId: '', qty: '' }])
                                }
                              >
                                เพิ่ม lot
                              </button>
                              <button
                                type="submit"
                                className="cap-button cap-button--primary"
                                data-testid="signal-replenish-submit"
                                disabled={busy || toKanbanContainerLotInputs(lots) === null}
                              >
                                ยืนยันเติมของแล้ว
                              </button>
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid="signal-replenish-close"
                                onClick={closeRowForm}
                              >
                                ปิด
                              </button>
                            </form>
                          ) : null}

                          {openForm?.transition === 'cancel' ? (
                            <form
                              className="cap-form cap-form--inline"
                              data-testid={`signal-cancel-form-${signal.signal_id}`}
                              aria-label="ยกเลิกสัญญาณ"
                              onSubmit={(event) => {
                                event.preventDefault();
                                void submitCancel(signal);
                              }}
                            >
                              <textarea
                                className="cap-input"
                                data-testid="signal-cancel-reason"
                                placeholder="เหตุผลที่ยกเลิก — บังคับกรอก"
                                value={cancellationReason}
                                onChange={(event) => setCancellationReason(event.target.value)}
                              />
                              <button
                                type="submit"
                                className="cap-button cap-button--primary"
                                data-testid="signal-cancel-submit"
                                disabled={busy || !isValidCancellationReason(cancellationReason)}
                              >
                                ยืนยันยกเลิก
                              </button>
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid="signal-cancel-close"
                                onClick={closeRowForm}
                              >
                                ปิด
                              </button>
                            </form>
                          ) : null}

                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="signal-action-failure-slot">
                              <SignalFailureNotice
                                failure={outcome.failure}
                                context={outcome.transition}
                                onRetry={() => setReloadToken((token) => token + 1)}
                              />
                              <p className="cap-notice__note">
                                ปุ่มที่กดคือ &quot;{signalActionLabel(outcome.transition)}&quot;
                                ซึ่งพาสัญญาณไปที่{' '}
                                {signalStatusLabel(signalActionTargetStatus(outcome.transition))}
                              </p>
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
